<template>
  <v-card>
    <v-progress-linear
      absolute
      :active="loading"
      indeterminate
      color="primary"
      bottom
    ></v-progress-linear>
    <v-card-title>
      <span class="headline">Create Bold Listing</span>
    </v-card-title>

    <v-card-text>
      <v-container>
        <v-row v-if="!isEditMode">
          <v-col cols="12">
            <v-autocomplete
              v-model="selectedFreeListingId"
              :items="freeListingOptions"
              :loading="freeListingLoading"
              :search-input.sync="freeListingSearch"
              item-text="label"
              item-value="id"
              clearable
              hide-details="auto"
              label="Search Free Listing to Prefill"
              no-data-text="Type at least 2 characters"
              @change="onFreeListingSelected"
            ></v-autocomplete>
          </v-col>
        </v-row>

        <v-alert
          v-if="!isEditMode && !data.free_listing_id"
          dense
          outlined
          type="info"
          class="mb-4"
        >
          Search and select a free listing first. The free listing details will
          be prefilled below.
        </v-alert>

        <bold-listing-form
          v-if="isEditMode || data.free_listing_id"
          :data="data"
          :listing-fields-readonly="!isEditMode"
        ></bold-listing-form>
      </v-container>
    </v-card-text>
    <form-errors :errors="errors"></form-errors>
    <v-card-actions>
      <v-spacer></v-spacer>
      <v-btn color="blue darken-1" text @click="_close">Cancel</v-btn>
      <v-btn
        :disabled="loading"
        v-if="isEditMode"
        color="blue darken-1"
        text
        @click="update"
        >Update</v-btn
      >
      <v-btn
        :disabled="loading || (!isEditMode && !data.free_listing_id)"
        v-else
        color="blue darken-1"
        text
        @click="create"
        >Create</v-btn
      >
    </v-card-actions>
  </v-card>
</template>

<script>
import BoldListingForm from './BoldListingForm'
import { axios } from '../../../utils'
import FormErrors from '../../../components/FormErrors'

const LOG_PREFIX = '[BoldListingsDialog]'

export default {
  name: 'BoldListingDialog',
  data() {
    return {
      data: {
        id: null,
        free_listing_id: null,
        business_name: '',
        email: '',
        website: '',
        address: '',
        telephone: '',
        mobile: '',
        category: '',
        suburb: '',
        about: '',
        services: '',
        active: false,
        verified: false,
        start_date: '',
        end_date: '',
        image: null
      },
      selectedFreeListingId: null,
      freeListingSearch: '',
      freeListingSearchTimer: null,
      freeListingLoading: false,
      freeListingOptions: [],
      loading: false,
      errors: []
    }
  },
  props: ['close', 'bold_listings', 'item'],
  components: {
    BoldListingForm,
    FormErrors
  },
  computed: {
    isEditMode() {
      return !!(this.item && this.item.id)
    }
  },
  mounted() {
    if (this.isEditMode) {
      this.loading = true
      console.info(`${LOG_PREFIX} loading existing bold listing`, {
        id: this.item && this.item.id ? this.item.id : null
      })
      axios
        .get('api/admin/bold-listings/' + this.item.id)
        .then(res => {
          this.data = {
            ...this.data,
            ...res.data,
            active: !!res.data.active,
            verified: !!res.data.verified,
            image: null
          }
          this.selectedFreeListingId = res.data.free_listing_id || null
          console.info(`${LOG_PREFIX} existing bold listing loaded`, {
            id: this.item && this.item.id ? this.item.id : null,
            freeListingId: this.selectedFreeListingId
          })
        })
        .catch(err => {
          console.error(`${LOG_PREFIX} failed to load existing bold listing`, {
            id: this.item && this.item.id ? this.item.id : null,
            status: err && err.response ? err.response.status : null,
            message: err && err.message ? err.message : 'unknown'
          })
        })
        .finally(() => (this.loading = false))
    }
  },
  methods: {
    searchFreeListings(query) {
      if (!query || query.trim().length < 2) {
        this.freeListingOptions = []
        return
      }

      this.freeListingLoading = true
      console.info(`${LOG_PREFIX} searching free listings`, { query })
      axios
        .get(
          encodeURI(
            `api/admin/free-listings?page=1&itemsPerPage=10&search=${query}&email=&category=&suburb=`
          )
        )
        .then(res => {
          const rows = (res.data && res.data.data) || []
          this.freeListingOptions = rows.map(row => ({
            id: row.id,
            label: row.email
              ? `${row.business_name} (${row.email})`
              : `${row.business_name}`
          }))
          console.info(`${LOG_PREFIX} free listing search loaded`, {
            query,
            results: this.freeListingOptions.length
          })
        })
        .catch(err => {
          console.error(`${LOG_PREFIX} free listing search failed`, {
            query,
            status: err && err.response ? err.response.status : null,
            message: err && err.message ? err.message : 'unknown'
          })
        })
        .finally(() => {
          this.freeListingLoading = false
        })
    },
    onFreeListingSelected(id) {
      if (!id) {
        console.info(`${LOG_PREFIX} free listing selection cleared`)
        this.data.free_listing_id = null
        this.data.business_name = ''
        this.data.email = ''
        this.data.website = ''
        this.data.address = ''
        this.data.telephone = ''
        this.data.mobile = ''
        this.data.category = ''
        this.data.suburb = ''
        this.data.verified = false
        this.data.active = false
        return
      }

      this.loading = true
      console.info(`${LOG_PREFIX} free listing selected`, { id })
      axios
        .get(`api/admin/free-listings/${id}`)
        .then(res => {
          const listing = res.data || {}
          this.data.free_listing_id = listing.id || id
          this.data.business_name = listing.business_name || ''
          this.data.email = listing.email || ''
          this.data.website = listing.website || ''
          this.data.address = listing.address || ''
          this.data.telephone = listing.telephone || ''
          this.data.mobile = listing.mobile || ''
          this.data.category = listing.category || ''
          this.data.suburb = listing.suburb || ''
          this.data.verified = !!listing.verified
          this.data.active = !!listing.active
          console.info(`${LOG_PREFIX} free listing details prefilled`, {
            freeListingId: this.data.free_listing_id
          })
        })
        .catch(err => {
          console.error(`${LOG_PREFIX} failed to prefill free listing`, {
            id,
            status: err && err.response ? err.response.status : null,
            message: err && err.message ? err.message : 'unknown'
          })
        })
        .finally(() => (this.loading = false))
    },
    buildFormData() {
      const formData = new FormData()

      for (const key in this.data) {
        if (key === 'image') {
          continue
        }
        if (key === 'active' || key === 'verified') {
          formData.append(key, this.data[key] ? 1 : 0)
        } else {
          formData.append(key, this.data[key] == null ? '' : this.data[key])
        }
      }

      if (this.data.image) {
        formData.append('image', this.data.image)
      }

      return formData
    },
    create() {
      if (!this.data.free_listing_id) {
        console.warn(`${LOG_PREFIX} create blocked: missing free_listing_id`)
        this.errors = [['Please select a free listing first.']]
        return
      }

      this.loading = true
      console.info(`${LOG_PREFIX} creating bold listing`, {
        freeListingId: this.data.free_listing_id
      })
      const formData = this.buildFormData()
      axios
        .post('api/admin/bold-listings/create', formData)
        .then(() => {
          console.info(`${LOG_PREFIX} bold listing created`)
          this._close()
        })
        .catch(err => {
          if (err.response && err.response.status === 422) {
            this.errors = Object.values(err.response.data.errors)
            console.warn(`${LOG_PREFIX} create validation failed`, {
              errors: this.errors
            })
          } else {
            console.error(`${LOG_PREFIX} create failed`, {
              status: err && err.response ? err.response.status : null,
              message: err && err.message ? err.message : 'unknown'
            })
          }
        })
        .finally(() => (this.loading = false))
    },
    update() {
      this.loading = true
      console.info(`${LOG_PREFIX} updating bold listing`, {
        id: this.item && this.item.id ? this.item.id : null
      })
      const formData = this.buildFormData()
      formData.append('_method', 'PUT')
      axios
        .post(`api/admin/bold-listings/${this.item.id}`, formData)
        .then(() => {
          console.info(`${LOG_PREFIX} bold listing updated`, {
            id: this.item && this.item.id ? this.item.id : null
          })
          this._close()
        })
        .catch(err => {
          if (err.response && err.response.status === 422) {
            this.errors = Object.values(err.response.data.errors)
            console.warn(`${LOG_PREFIX} update validation failed`, {
              errors: this.errors
            })
          } else {
            console.error(`${LOG_PREFIX} update failed`, {
              id: this.item && this.item.id ? this.item.id : null,
              status: err && err.response ? err.response.status : null,
              message: err && err.message ? err.message : 'unknown'
            })
          }
        })
        .finally(() => (this.loading = false))
    },
    _close() {
      console.info(`${LOG_PREFIX} dialog close`)
      this.clearData()
      this.close()
    },
    clearData() {
      this.data = {
        id: null,
        free_listing_id: null,
        business_name: '',
        email: '',
        website: '',
        address: '',
        telephone: '',
        mobile: '',
        category: '',
        suburb: '',
        about: '',
        services: '',
        active: false,
        verified: false,
        start_date: '',
        end_date: '',
        image: null
      }
      this.selectedFreeListingId = null
      this.freeListingSearch = ''
      this.freeListingOptions = []
      this.errors = []
    }
  },
  watch: {
    freeListingSearch(value) {
      if (this.freeListingSearchTimer) {
        clearTimeout(this.freeListingSearchTimer)
      }
      this.freeListingSearchTimer = setTimeout(() => {
        this.searchFreeListings(value)
      }, 250)
    }
  }
}
</script>

<style lang="scss" scoped></style>
